import { Link } from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Archive, ArrowUpCircle, Globe2, Plus, Upload } from 'lucide-react';
import { Button, ConfirmDialog, Drawer, Fact, Facts, PlatformIcon } from '@/components/ui';
import { useCaptureTemplate, useTemplatePublishing } from '@/hooks/useGeo';
import { useDeprecateTemplate, useRollout } from '@/hooks/useTemplates';
import { toast } from '@/store/toastStore';
import { errorText, failureText } from '@/utils/errors';
import { formatDateTime, formatRelative, plural } from '@/utils/format';
import { idempotencyKey } from '@/utils/idempotency';
import { queryKeys } from '@/lib/queryKeys';
import type { TemplateFamilyView } from '@/types/api';

/**
 * One template (ADR-0047): what it looks like, where its structure is edited,
 * the sites made from it, and its versions — with the three things done to it:
 * a new GEO, the next version from the reference site, and a rollout.
 */
export function TemplateFamilyDrawer({
  family,
  onClose,
  onNewGeo,
  onRolledOut,
  mayManage,
  mayDeploy,
  mayCreate,
}: {
  readonly family: TemplateFamilyView | null;
  readonly onClose: () => void;
  readonly onNewGeo: (templateId: string) => void;
  readonly onRolledOut: (runId: string) => void;
  readonly mayManage: boolean;
  readonly mayDeploy: boolean;
  readonly mayCreate: boolean;
}): ReactNode {
  // Held after `family` goes null so the closing animation has something to draw.
  const [last, setLast] = useState<TemplateFamilyView | null>(null);
  if (family && family !== last) setLast(family);
  const data = family ?? last;
  const [confirming, setConfirming] = useState<'rollout' | 'retire' | null>(null);

  const rollout = useRollout({
    onSuccess: (run) => {
      setConfirming(null);
      toast.success('Обновление сайтов запущено', 'Сначала на одном сайте, затем на остальных');
      onRolledOut(run.id);
    },
  });
  const capture = useCaptureTemplate(data?.referenceSite?.id ?? '', {
    onError: (error) => toast.error('Не удалось опубликовать версию', errorText(error)),
  });
  // The button says what is happening until the version is here; then the
  // list and the rollout offer show it. A reference site with nothing changed
  // since the current version makes none, and is told so.
  const queryClient = useQueryClient();
  const publishedOver = useRef<string | null>(null);
  const publishing = useTemplatePublishing(family?.referenceSite?.id ?? null, (job) => {
    const over = publishedOver.current;
    publishedOver.current = null;
    if (job.state !== 'completed') {
      toast.error('Не удалось опубликовать версию', job.errorCode ? failureText(job.errorCode) : 'Подробности — в истории эталона');
      return;
    }
    const now = queryClient.getQueryData<TemplateFamilyView[]>(queryKeys.templates.families)?.find((item) => item.id === data?.id)?.latest;
    if (now && now.id === over) toast.info('Изменений нет', `Эталон совпадает с версией ${now.version}`);
    else toast.success(now ? `Опубликована версия ${now.version}` : 'Новая версия опубликована', 'Сайты на прежних версиях можно обновить здесь же');
  });
  const latestId = data?.latest.id ?? null;
  useEffect(() => {
    if (publishing && publishedOver.current === null) publishedOver.current = latestId;
  }, [publishing, latestId]);
  const retire = useDeprecateTemplate({
    onSuccess: () => {
      setConfirming(null);
      toast.success('Версия снята с публикации');
    },
  });

  if (!data) return null;
  const latest = data.latest;
  const build = latest.build;

  return (
    <Drawer
      open={family !== null}
      title={data.name}
      description={build ? `Версия ${latest.version} · ${plural(data.siteCount, 'сайт', 'сайта', 'сайтов')}` : `Образ ${latest.version}`}
      onClose={onClose}
      width="md"
      footer={
        <>
          {mayManage && build && !latest.deprecated ? (
            <Button variant="danger" icon={Archive} onClick={() => setConfirming('retire')}>
              Снять версию
            </Button>
          ) : null}
          {mayCreate && build && !latest.deprecated ? (
            <Button variant="primary" icon={Plus} onClick={() => onNewGeo(data.id)}>
              Новое ГЕО
            </Button>
          ) : null}
        </>
      }
    >
      <div className="stack stack-loose">
        {data.preview ? (
          <img className="template-shot" src={`/api/v1/sites/${data.preview.siteId}/preview?t=${encodeURIComponent(data.preview.takenAt)}`} alt={`Сайт на шаблоне ${data.name}`} />
        ) : null}

        {build && data.outdatedCount > 0 && mayDeploy ? (
          <div className="callout">
            <div className="stack stack-tight">
              <span className="strong">{plural(data.outdatedCount, 'сайт', 'сайта', 'сайтов')} на прежних версиях</span>
              <span className="muted text-sm">Обновление идёт сначала на одном сайте; при сбое сайт возвращается к прежней версии.</span>
            </div>
            <Button variant="primary" icon={ArrowUpCircle} onClick={() => setConfirming('rollout')}>
              Обновить до {latest.version}
            </Button>
          </div>
        ) : null}

        <Facts>
          <Fact label="Платформа">
            <span className="platform-name">
              <PlatformIcon platform="wordpress" />
              WordPress
            </span>
          </Fact>
          {build ? (
            <Fact label="Эталон">
              {data.referenceSite ? (
                <span className="stack stack-tight">
                  <Link to={`/sites/${data.referenceSite.id}`}>{data.referenceSite.name}</Link>
                  <span className="muted text-sm">Меняйте тему, плагины и меню в админке эталона, затем опубликуйте версию</span>
                </span>
              ) : (
                <span className="faint">удалён</span>
              )}
            </Fact>
          ) : null}
          <Fact label="Сайтов">{data.siteCount}</Fact>
          <Fact label="Опубликована">{formatDateTime(latest.createdAt)}</Fact>
        </Facts>

        {mayManage && build && data.referenceSite ? (
          <div>
            <Button
              icon={Upload}
              busy={capture.isPending || publishing}
              disabled={publishing}
              onClick={() => capture.mutate({ input: { templateId: data.id }, key: idempotencyKey('template.capture') })}
            >
              {publishing ? 'Публикуется новая версия…' : 'Опубликовать изменения эталона'}
            </Button>
          </div>
        ) : null}

        <section className="stack stack-tight">
          <span className="field-label">Версии</span>
          <ol className="version-list">
            {data.versions.map((version) => (
              <li key={version.id} className={version.deprecated ? 'version-retired' : undefined}>
                <div className="row-between">
                  <span className="row-tight">
                    <span className="strong mono">{version.version}</span>
                    {version.id === latest.id ? <span className="tag">текущая</span> : null}
                    {version.deprecated ? <span className="tag">снята</span> : null}
                  </span>
                  <span className="muted text-sm" title={formatDateTime(version.createdAt)}>
                    {plural(version.siteCount, 'сайт', 'сайта', 'сайтов')} · {formatRelative(version.createdAt)}
                  </span>
                </div>
                {version.changes.length > 0 ? (
                  <ul className="version-changes">
                    {version.changes.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ol>
        </section>

        {!build ? (
          <p className="field-hint row-tight">
            <Globe2 size={13} aria-hidden="true" />
            Шаблон-образ: задаёт версию WordPress и PHP для новых сайтов, без темы и структуры.
          </p>
        ) : null}
      </div>

      <ConfirmDialog
        open={confirming === 'rollout'}
        title={`Обновить ${plural(data.outdatedCount, 'сайт', 'сайта', 'сайтов')} до ${latest.version}?`}
        description="Перед обновлением каждого сайта создаётся копия. Сначала обновляется один сайт; после двух сбоев остальные не трогаются."
        confirmLabel="Обновить"
        busy={rollout.isPending}
        error={rollout.isError ? errorText(rollout.error, 'Не удалось запустить обновление') : undefined}
        onConfirm={() => rollout.mutate({ templateId: data.id, versionId: latest.id })}
        onClose={() => setConfirming(null)}
      />
      <ConfirmDialog
        open={confirming === 'retire'}
        title={`Снять версию ${latest.version}?`}
        description="Новые сайты будут создаваться из предыдущей версии. Существующие сайты продолжат работать."
        confirmLabel="Снять версию"
        destructive
        busy={retire.isPending}
        error={retire.isError ? errorText(retire.error, 'Не удалось снять версию') : undefined}
        onConfirm={() => retire.mutate(latest.id)}
        onClose={() => setConfirming(null)}
      />
    </Drawer>
  );
}
